import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RevealOnView } from '@/components/ui';
import type { SolutionSection } from '@/pages/Products/content';
import { CONTENT_SLIDE, HEADER_FADE, REVEAL_TIMING, useInView } from '@/utils/animation';
import { ProductRow } from './ProductRow';
import { SectionHeader } from './SectionHeader';

interface LensCareSectionProps {
  section: SolutionSection;
  /** When true, paints the section background with the same tint as the hero (#E2DFDA at 20%). */
  tinted?: boolean;
}

/**
 * Lens care section — same layout as ProductSection with a single SolutionProduct row.
 *
 * Figma (large, 1440px):
 *   - 200px section padding, 1200 max content
 *   - Header: 2-col 440 title + 600 intro
 *   - Full-width 1px hairline divider below header (120 above, 121 below)
 *   - Single Eyedia row (no eyebrow, no chip line; "Solution specifications" panel)
 *
 * Solution spec panel uses 2 columns at lg+ (vs 3 for lens products).
 */
// Reveal cascade — descriptor starts 25% into the header fade (see
// SectionHeader); row then starts 30% into the descriptor's slide.
const INTRO_DELAY_MS = Math.round(HEADER_FADE.durationMs * 0.25);
const ROW_DELAY_MS = INTRO_DELAY_MS + Math.round(REVEAL_TIMING.durationMs * 0.3);

export function LensCareSection({ section, tinted = false }: LensCareSectionProps) {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { threshold: 0 });
  // 100ms beat after the section first enters view before the cascade fires.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, [inView]);

  return (
    <section
      ref={sectionRef}
      id={section.id}
      aria-labelledby={`${section.id}-heading`}
      className={`scroll-mt-[160px] px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30 ${
        tinted ? 'bg-clearlab-divider-light/20' : ''
      }`}
    >
      <div className="mx-auto flex w-full max-w-content-xl flex-col gap-30">
        <SectionHeader
          id={`${section.id}-heading`}
          title={t(`products.sections.${section.sectionKey}.title`)}
          intro={t(`products.sections.${section.sectionKey}.intro`)}
          layout="fluid"
          visible={visible}
        />

        <RevealOnView
          {...CONTENT_SLIDE}
          inView={visible}
          delayMs={ROW_DELAY_MS}
          className="flex flex-col gap-30"
        >
          <hr className="w-full border-0 border-t border-clearlab-divider-darker/60" />

          <ProductRow
            product={section.product}
            specsLabel={t('products.labels.solutionSpecifications')}
            specsColumns={2}
          />
        </RevealOnView>
      </div>
    </section>
  );
}
